
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { MissionGoalBox } from "./components/MissionGoalBox";
import { FibSelector } from "./components/FibSelector";
import { ResultBox } from "./components/ResultBox";
import { NameDialog } from "./components/NameDialog";
import { saveScoreApi } from "./api/api";
import {
  getLevelPrimes, generateFibsUpTo, hasAdjacent, sumSelected,
} from "./helpers/spacesheepHelpers";
import {
  getMaxLevelUnlocked, saveMaxLevelUnlocked,
  getInitialLevel, saveCurrentLevel,
  patchGameState, loadGameState, clearGameState
} from "./helpers/storageHelpers";
import { WarpJump } from "./components/svg/WarpJump";
import { motion, useAnimation } from "framer-motion";
import { sfx } from "./helpers/sfx";
import { fireConfetti, fireFireworks } from "./helpers/confettiFx";
import { pushToast } from "./components/Toaster";
import { Flame, Timer } from "lucide-react";



export function GameBox() {

  const [maxLevelUnlocked, setMaxLevelUnlocked] = useState(getMaxLevelUnlocked());
  const [level, setLevel] = useState(getInitialLevel());
  const [primes, setPrimes] = useState<[bigint, bigint, bigint] | null>(null);
  const [playerName, setPlayerName] = useState(localStorage.getItem("spacesheep-name") || "");
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [selectedShips, setSelectedShips] = useState<boolean[]>([]);
  const [selectedSheep, setSelectedSheep] = useState<boolean[]>([]);
  const [message, setMessage] = useState<string>("");
  const [warp, setWarp] = useState(false);
  const [streak, setStreak] = useState<number>(() => Number(localStorage.getItem("ss-streak") || 0));
  const [elapsed, setElapsed] = useState(0);
  const startTimeRef = useRef<number>(Date.now());


  const sendBtnControls = useAnimation();
  const resetBtnControls = useAnimation();

  useEffect(() => {
    const cached = loadGameState(level);
    if (cached && cached.p && cached.q && cached.N) {
      setPrimes([BigInt(cached.p), BigInt(cached.q), BigInt(cached.N)]);
      return; // już mamy w cache
    }

    let cancelled = false;
    setPrimes(null); // pokazuje loader

    getLevelPrimes(level).then(([p, q, N]) => {
      if (!cancelled) {
        setPrimes([p, q, N]);
        // ⬇️ TU zapisujesz wygenerowane primes do localStorage!
        patchGameState(level, {
          p: p.toString(),
          q: q.toString(),
          N: N.toString(),
        });
      }
    });

    return () => { cancelled = true; };
  }, [level]);

  // Obliczanie fibs
  const fibs = useMemo(() => {
    if (!primes) return [];
    const [p, q] = primes;
    return generateFibsUpTo(p > q ? p : q);
  }, [primes]);

  // Odtwarzanie stanu planszy z localStorage
  useEffect(() => {
    const state = loadGameState(level);
    if (state && fibs.length) {
      setSelectedShips(state.selectedShips ?? Array(fibs.length).fill(false));
      setSelectedSheep(state.selectedSheep ?? Array(fibs.length).fill(false));
      setMessage(state.message ?? "");
    } else {
      setSelectedShips(Array(fibs.length).fill(false));
      setSelectedSheep(Array(fibs.length).fill(false));
      setMessage("");
    }
  }, [level, fibs.length, primes]);

  // Zapis stanu do localStorage
  useEffect(() => {
    if (level && fibs.length) {
      patchGameState(level, { selectedShips, selectedSheep, message });
    }
  }, [selectedShips, selectedSheep, message, fibs.length]);


  // Liczenie sum i wyniku
  const shipsSum = sumSelected(selectedShips, fibs);
  const sheepSum = sumSelected(selectedSheep, fibs);
  const N = primes ? primes[2] : 1n;
  const wynik = shipsSum * sheepSum;
  const doCelu = N - wynik;

  const handleShip = useCallback((i: number) =>
    setSelectedShips(prev => {
      const copy = [...prev];
      if (!prev[i] && ((prev[i - 1] ?? false) || (prev[i + 1] ?? false))) {
        sfx.error();
        return prev;
      }
      copy[i] = !copy[i];
      copy[i] ? sfx.select() : sfx.deselect();
      return copy;
    }), []);
  const handleSheep = useCallback((i: number) =>
    setSelectedSheep(prev => {
      const copy = [...prev];
      if (!prev[i] && ((prev[i - 1] ?? false) || (prev[i + 1] ?? false))) {
        sfx.error();
        return prev;
      }
      copy[i] = !copy[i];
      copy[i] ? sfx.select() : sfx.deselect();
      return copy;
    }), []);

  // Speed-run timer per level
  useEffect(() => {
    startTimeRef.current = Date.now();
    setElapsed(0);
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 250);
    return () => window.clearInterval(id);
  }, [level, primes]);


  // CHECK WIN
  const checkWin = async () => {

    await sendBtnControls.start({
      scale: [1, 1.25, 0.92, 1],
      transition: { duration: 0.6 }
    });

    if (shipsSum === 0n) { sfx.error(); return setMessage("Musisz wybrać co najmniej jeden statek!"); }
    if (sheepSum === 0n) { sfx.error(); return setMessage("Musisz wybrać co najmniej jedną partię owiec!"); }
    if (hasAdjacent(selectedShips)) { sfx.error(); return setMessage("Nie można wybrać dwóch sąsiadujących statków!"); }
    if (hasAdjacent(selectedSheep)) { sfx.error(); return setMessage("Nie można wybrać dwóch sąsiadujących partii owiec!"); }
    if (wynik !== N) {
      sfx.error();
      setStreak(0);
      try { localStorage.setItem("ss-streak", "0"); } catch {}
      return setMessage(`Załadunek nie zgadza się! Masz ${shipsSum} × ${sheepSum} = ${wynik} (${doCelu > 0n ? `brakuje ${doCelu}` : `przekroczono o ${-doCelu}`})`);
    }
    setMessage(`✅ MISJA UDANA! Wysłano dokładnie ${N} owiec do nowej galaktyki!`);
    sfx.success();
    fireConfetti();

    const timeSec = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const newStreak = streak + 1;
    setStreak(newStreak);
    try { localStorage.setItem("ss-streak", String(newStreak)); } catch {}

    // Achievement toasts
    pushToast({ emoji: "🎆", title: `Poziom ${level} ukończony!`, body: `Czas: ${timeSec}s`, variant: "success" });
    if (newStreak === 3) pushToast({ emoji: "🔥", title: "3 z rzędu!", body: "Świetna passa, kapitanie", variant: "success" });
    if (newStreak === 5) pushToast({ emoji: "⚡", title: "5 z rzędu!", body: "Niesamowite combo", variant: "success" });
    if (newStreak === 10) { pushToast({ emoji: "💎", title: "10 z rzędu! LEGENDA", variant: "success" }); fireFireworks(); }
    if (timeSec < 15) pushToast({ emoji: "🏁", title: "Speed Run", body: `Ponże 15s (${timeSec}s)`, variant: "success" });
    if (level === maxLevelUnlocked) pushToast({ emoji: "🔓", title: `Odblokowano poziom ${level + 1}!`, variant: "info" });

    if (!playerName.trim()) { setShowNameDialog(true); return; }

    const save = saveScoreApi({ name: playerName, level, N });
    sfx.warp();
    setWarp(true);
    setTimeout(async () => {
      await save;
      if (level === maxLevelUnlocked) {
        const next = level + 1;
        setMaxLevelUnlocked(next);
        saveMaxLevelUnlocked(next);
      }
      saveCurrentLevel(level + 1);
      setLevel(level + 1);
      clearGameState(level);
      setWarp(false);
    }, 2500);
  };

  // UI
  if (!primes)
    return (
      <div className="loader-wrapper">
        <div className="planet-loader">🪐</div>
        <div className="loader-title">Generuję poziom {level}…</div>
        <div className="loader-bar" />
        <div className="loader-sub">To może potrwać kilka sekund przy bardzo dużych liczbach pierwszych.</div>
      </div>
    );

  return (
    <div>
      <div className="background-planet" />
      {!warp &&

        <div className="main-wrapper">
          {playerName && (
            <div className="player-greeting">
              ▸ Cześć, {playerName}!
            </div>
          )}
          <div className="hud-row">
            <div className={"hud-pill streak" + (streak >= 3 ? " hot" : "")}>
              <Flame size={14} /> Combo <b>{streak}</b>
            </div>
            <div className="hud-pill timer">
              <Timer size={14} /> {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
            </div>
          </div>
          <NameDialog
            open={showNameDialog}
            initialName={playerName}
            onSave={async name => {
              setPlayerName(name);
              localStorage.setItem("spacesheep-name", name);
              setShowNameDialog(false);
              const save = saveScoreApi({ name, level, N });
              setWarp(true);
              setTimeout(async () => {
                await save;
                if (level === maxLevelUnlocked) {
                  const next = level + 1;
                  setMaxLevelUnlocked(next);
                  saveMaxLevelUnlocked(next);
                }
                saveCurrentLevel(level + 1);
                setLevel(level + 1);
                clearGameState(level);
                setWarp(false);

              }, 2500);
            }}
            onClose={() => setShowNameDialog(false)}
          />
          <div className="level-select-row">
            <button
              className="level-btn"
              onClick={() => { clearGameState(level); saveCurrentLevel(Math.max(1, level - 1)); setLevel(Math.max(1, level - 1)); }}
              disabled={level <= 1}
            >−</button>
            <span className="level-badge">{level}</span>
            <button
              className="level-btn"
              onClick={() => { clearGameState(level); saveCurrentLevel(Math.min(maxLevelUnlocked, level + 1)); setLevel(Math.min(maxLevelUnlocked, level + 1)); }}
              disabled={level >= maxLevelUnlocked}
            >+</button>
            <span className="max-level">(Najwyższy odblokowany: <b>{maxLevelUnlocked}</b>)</span>
          </div>
          <MissionGoalBox N={N} />
          <div style={{ display: "flex", gap: 44, justifyContent: "center", flexWrap: "wrap" }}>
            <FibSelector
              values={fibs}
              selected={selectedShips}
              onToggle={handleShip}
              label="Statki – wybierz liczebność floty"
              type="ship"
            />
            <FibSelector
              values={fibs}
              selected={selectedSheep}
              onToggle={handleSheep}
              label="Partie owiec – wybierz pojemność statku"
              type="sheep"
            />
          </div>
          <div className="action-row">
            <motion.button className="send-btn"
              animate={sendBtnControls}
              onClick={checkWin}
            >
              ▸ Wyślij stado
            </motion.button>
            <motion.button className="reset-btn"
              onClick={async () => {
                const annimate = resetBtnControls.start({
                  scale: [1, 1.25, 0.92, 1],
                  transition: { duration: 0.6 }
                });
                setSelectedShips(Array(fibs.length).fill(false));
                setSelectedSheep(Array(fibs.length).fill(false));
                setMessage("");
                clearGameState(level);
                await annimate;
              }}
              animate={resetBtnControls}
            >
              ↺ Reset
            </motion.button>
          </div>
          <ResultBox
            shipsSum={shipsSum}
            sheepSum={sheepSum}
            N={N}
            doCelu={doCelu}
            wynik={wynik}
            message={message}
          />
          <div className="hint-text">
            Liczby rosną automatycznie z każdym poziomem. Zawsze możesz wrócić do niższego.
          </div>
        </div>}
      <WarpJump nextLevelText={`Poziom ${level + 1}`} active={warp} onEnd={() => setWarp(false)} />

    </div>
  );
}
